import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";
import {
  DevelopmentUpdatesApiResponse,
  DevelopmentUpdate,
  DevelopmentUpdatesError,
  Buyer,
} from "../types/development-updates-types";

interface AuthenticatePayload {
  PlotNumber: string;
  password: string;
  projectId: number;
}

export const authenticateDevelopmentUpdates = createAsyncThunk(
  "developmentUpdates/authenticate",
  async (payload: AuthenticatePayload, { rejectWithValue }) => {
    try {
      const endpoint = "/developments-updates/authenticate";
      const requestBody = {
        PlotNumber: payload.PlotNumber,
        password: payload.password,
        projectId: payload.projectId,
      };

      const response = await axiosInstance.post<
        DevelopmentUpdatesApiResponse | DevelopmentUpdatesError
      >(endpoint, requestBody);

      // Check if response contains error
      if ("error" in response.data) {
        const errorData = response.data as DevelopmentUpdatesError;
        return rejectWithValue(
          errorData.error.message || "Authentication failed"
        );
      }

      return response.data as DevelopmentUpdatesApiResponse;
    } catch (error: any) {
      const errorResponse = error.response?.data as
        | DevelopmentUpdatesError
        | undefined;
      const errorMessage =
        errorResponse?.error?.message ||
        error.message ||
        "Something went wrong";
      return rejectWithValue(errorMessage);
    }
  }
);

type DevelopmentUpdatesState = {
  updates: DevelopmentUpdate[];
  buyer: Buyer | null;
  project: DevelopmentUpdate["project"] | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
};

const initialState: DevelopmentUpdatesState = {
  updates: [],
  buyer: null,
  project: null,
  status: "idle",
  error: null,
};

const developmentUpdatesSlice = createSlice({
  name: "developmentUpdates",
  initialState,
  reducers: {
    clearDevelopmentUpdates: (state) => {
      state.updates = [];
      state.buyer = null;
      state.project = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateDevelopmentUpdates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(authenticateDevelopmentUpdates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.updates = action.payload.data;
        // Extract buyer and project from first update (they're shared)
        if (action.payload.data.length > 0) {
          state.buyer = action.payload.data[0].buyer;
          state.project = action.payload.data[0].project;
        }
      })
      .addCase(authenticateDevelopmentUpdates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { clearDevelopmentUpdates } = developmentUpdatesSlice.actions;
export default developmentUpdatesSlice.reducer;

